import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../../../server";

const defaultSettings = {
  enabled: false,
  type: "percentage",
  value: 10,
  stackable: true,
};

const VeteranDiscountSettings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [stacking, setStacking] = useState({ allowStacking: true, bundleStackable: true });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`${server}/options/global`, {
          withCredentials: true,
        });
        const vd = data?.options?.veteranDiscount;
        const ds = data?.options?.discountStacking;
        if (ds) setStacking({ allowStacking: ds.allowStacking !== false, bundleStackable: ds.bundleStackable !== false });
        setSettings({
          enabled: !!vd?.enabled,
          type: vd?.type === "fixed" ? "fixed" : "percentage",
          value: typeof vd?.value === "number" ? vd.value : defaultSettings.value,
          stackable: vd?.stackable !== false,
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await axios.put(
        `${server}/options/global`,
        { veteranDiscount: settings, discountStacking: stacking },
        { withCredentials: true }
      );
      toast.success("Veteran discount and stacking settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-slate-500">Loading veteran discount settings…</div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Veteran discount</h2>
        <p className="text-sm text-slate-500 mt-1">
          Apply a discount at checkout for users with verified veteran status. Only users who have submitted proof and been verified by an admin receive this discount.
        </p>
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, enabled: e.target.checked }))
            }
            className="w-4 h-4 rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
          />
          <span className="text-sm font-medium text-slate-700">Enable veteran discount</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.stackable}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, stackable: e.target.checked }))
            }
            className="w-4 h-4 rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
          />
          <span className="text-sm font-medium text-slate-700">Stackable (can combine with discount codes and bundle discounts)</span>
        </label>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Discount stacking (global)</h3>
        <p className="text-xs text-slate-500 mb-3">When stacking is allowed, discounts marked as stackable can be combined at checkout. When off, only the single best discount applies.</p>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stacking.allowStacking}
              onChange={(e) => setStacking((prev) => ({ ...prev, allowStacking: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
            />
            <span className="text-sm font-medium text-slate-700">Allow discount stacking</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stacking.bundleStackable}
              onChange={(e) => setStacking((prev) => ({ ...prev, bundleStackable: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
            />
            <span className="text-sm font-medium text-slate-700">Bundle discounts are stackable</span>
          </label>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Discount type</label>
          <select
            value={settings.type}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                type: e.target.value,
                value: e.target.value === "percentage" ? 10 : 5,
              }))
            }
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
          >
            <option value="percentage">Percentage off</option>
            <option value="fixed">Fixed amount off (£)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {settings.type === "percentage" ? "Percentage (%)" : "Amount (£)"}
          </label>
          <input
            type="number"
            min={0}
            max={settings.type === "percentage" ? 100 : undefined}
            step={settings.type === "percentage" ? 1 : 0.01}
            value={settings.value}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                value: parseFloat(e.target.value) || 0,
              }))
            }
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
          />
        </div>
      </div>

      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
        >
          {isSaving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
};

export default VeteranDiscountSettings;
